import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcSweepsClearingV1SweepsJobQueryServiceSweepsJobGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-clearing-v1-sweeps-job-query-service-sweeps-job-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcSweepsClearingV1SweepsJobState } from '@interxfi/types';
import { TableSectionHeader } from '../../../../../../../../../../components/table/table-section-header';
import { SweepsJobActivityReportAccountStatsTable } from '../../../../../../../../../../features/sweeps/clearing/job/sweeps-job-activity-report-account-stats-table';
import { SweepsJobActivityReportStatsTable } from '../../../../../../../../../../features/sweeps/clearing/job/sweeps-job-activity-report-stats-table';
import { SweepsJobEventDrawer, SweepsJobEventSummary } from '../../../../../../../../../../features/sweeps/clearing/job/sweeps-job-event';
import { getSweepsJobStateListItems } from '../../../../../../../../../../features/sweeps/clearing/job/sweeps-job-list-items';
import { getSweepsJobDetailPath } from '../../../../../../../../../../features/sweeps/clearing/job/sweeps-job-routing';
import { SweepsJobStatusPill } from '../../../../../../../../../../features/sweeps/clearing/job/sweeps-job-status-pill';
import { SweepsTasksTable } from '../../../../../../../../../../features/sweeps/clearing/task/sweeps-tasks-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/sweep/job/_job/$jobId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `sweep-job-${params.jobId}`,
        label: 'Job',
        to: getSweepsJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Sweep Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Sweep Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Sweep Job'),
  component: SweepJobDetail,
});

function getListItems(data: IxcSweepsClearingV1SweepsJobState | undefined) {
  const listItems = getSweepsJobStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [
          listItems.data.operationType,
          ...match(listItems.data.spec)
            .with({ activityReport: P.not(P.nullish) }, ({ activityReport }) => [
              activityReport.value.adapterOrderId,
              activityReport.value.confirmTimestamp,
              activityReport.value.currentCutoff,
              activityReport.value.previousCutoff,
            ])
            .with({ dividendsPay: P.not(P.nullish) }, ({ dividendsPay }) => [
              dividendsPay.value.endDate,
              dividendsPay.value.firstDayOfMonth,
              dividendsPay.value.sweepTargetId,
            ])
            .with({ dividendsCalculate: P.not(P.nullish) }, ({ dividendsCalculate }) => [
              dividendsCalculate.value.sweepTargetId,
              dividendsCalculate.value.assetId,
              dividendsCalculate.value.programId,
              dividendsCalculate.value.dividendDate,
              dividendsCalculate.value.dividendRate,
              dividendsCalculate.value.expectedPaymentDate,
              dividendsCalculate.value.dataHash,
            ])
            .with({ dividendsRecalculate: P.not(P.nullish) }, ({ dividendsRecalculate }) => [
              dividendsRecalculate.value.sweepTargetId,
              dividendsRecalculate.value.sweepPlanId,
              dividendsRecalculate.value.accountId,
              dividendsRecalculate.value.firstDayOfMonth,
              dividendsRecalculate.value.applyCorrections,
            ])
            .with({ balancesReconcile: P.not(P.nullish) }, ({ balancesReconcile }) => [
              balancesReconcile.value.accountId,
              balancesReconcile.value.fromDate,
              balancesReconcile.value.applyCorrections,
            ])
            .otherwise(() => []),
        ],
      },
      ...('dividendsRecalculate' in listItems.data.stats
        ? [
            {
              id: 'stats',
              title: 'Stats',
              items: [
                listItems.data.stats.dividendsRecalculate.value.totalChecked,
                listItems.data.stats.dividendsRecalculate.value.totalCorrected,
                listItems.data.stats.dividendsRecalculate.value.totalSkipped,
                listItems.data.stats.dividendsRecalculate.value.totalDividendsDiff,
                listItems.data.stats.dividendsRecalculate.value.totalSweptBalanceDiff,
              ],
            },
          ]
        : []),
      ...('balancesReconcile' in listItems.data.stats
        ? [
            {
              id: 'stats',
              title: 'Stats',
              items: [
                listItems.data.stats.balancesReconcile.value.totalChecked,
                listItems.data.stats.balancesReconcile.value.totalCorrected,
                listItems.data.stats.balancesReconcile.value.totalSkipped,
                listItems.data.stats.balancesReconcile.value.totalDrifting,
                listItems.data.stats.balancesReconcile.value.totalRefused,
                listItems.data.stats.balancesReconcile.value.totalAvailableCashDiff,
                listItems.data.stats.balancesReconcile.value.totalSweptCashDiff,
              ],
            },
          ]
        : []),
      {
        id: 'metrics',
        title: 'Metrics',
        items: [
          listItems.data.metrics.total,
          listItems.data.metrics.succeeded,
          listItems.data.metrics.failed,
          listItems.data.metrics.errored,
          listItems.data.metrics.pending,
          listItems.data.metrics.expectedTotal,
          listItems.data.metrics.receivedCount,
        ],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.sweepsJobId] as const satisfies ReadonlyListItem[],
  };
}

function getMenuItems(sweepsJobId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: SweepsJobEventDrawer,
        props: { request: { sweepsJobId } },
      },
    },
    actions: undefined,
  };
}

function TasksTableHeader(props: CustomTableHeaderProps) {
  return <TableSectionHeader {...props} title="Tasks" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load sweep job', 'view this sweep job');

function SweepJobDetail() {
  const { jobId } = Route.useParams();
  const request = { sweepsJobId: jobId } as const;
  const { data, error, isPending } = useIxcSweepsClearingV1SweepsJobQueryServiceSweepsJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.sweepsJob), [data?.sweepsJob]);
  const entityMenuItems = useMemo(() => getMenuItems(jobId), [jobId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <SweepsJobStatusPill isPending={showPending} size="large" status={data?.sweepsJob?.status} />
            </HorizontalStack>

            <EntityActionMenu {...entityMenuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <SweepsJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {data?.sweepsJob?.data?.stats && 'activityReport' in data.sweepsJob.data.stats ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Stats</Typography.Level3>
            <SweepsJobActivityReportStatsTable stats={data.sweepsJob.data.stats.activityReport} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.sweepsJob?.data?.stats && 'activityReport' in data.sweepsJob.data.stats ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Account Stats</Typography.Level3>
            <SweepsJobActivityReportAccountStatsTable stats={data.sweepsJob.data.stats.activityReport} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      <PageLayout.Section>
        <SweepsTasksTable HeaderActions={TasksTableHeader} sweepsJobId={jobId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
